<template>
  <v-container class="base-reviews-list">
    <v-row justify="center">
      <v-col cols="12" v-if="compLoaded">
        <v-row no-gutters v-if="facilitiesCount">
          <v-col cols="12">
            <v-row>
              <v-col>
                <div class="d-flex align-center full-width px-3">
                  <div class="searchbox">
                    <v-text-field
                      v-model="searchValue"
                      :label="$t('message.common.findPhrase')"
                      :prepend-inner-icon="
                        !searchMode ? 'icon-search' : 'icon-close'
                      "
                      outlined
                      rounded
                      :hide-details="true"
                      @click:prepend-inner="searchMode && clearSearch()"
                      @keydown.enter.prevent="searchValue && onReviewSearch()"
                    ></v-text-field>
                    <ui-button
                      color="primary"
                      class="ml-2 searchbox__btn"
                      :disabled="!searchValue"
                      @click="onReviewSearch"
                    >
                      {{ $t("message.common.search") }}
                    </ui-button>
                  </div>
                  <div class="counters">
                    <ItemsCounter
                      :count="totalCount"
                      class="counter"
                      :prefixText="basicCounterLabel"
                    ></ItemsCounter>
                    <ItemsCounter
                      v-if="showPercentage"
                      :count="percentageValue"
                      class="counter"
                      suffixText="message.reviews.percentageOfAllReviews"
                    ></ItemsCounter>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-tabs
                  v-model="active"
                  slider-color="primary"
                  active-class="black--text"
                  class="px-1"
                >
                  <v-tab
                    v-for="tab in tabs"
                    :key="tab.id"
                    :to="tab.path"
                    :disabled="loading"
                    ripple
                  >
                    {{ $t(tab.name) }}
                  </v-tab>
                </v-tabs>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <Pagination
                  class="flex-grow-1"
                  :page="page"
                  :per-page="perPage"
                  :options="perPageOptions"
                  :total-count="totalCount"
                  @change="handlePageChange"
                >
                  <div class="sort-type">
                    <v-select
                      v-model="sortType"
                      :items="sortTypes"
                      :label="$t('message.reviews.sort')"
                      item-text="label"
                      item-value="value"
                      :disabled="loading"
                      hide-details
                      dense
                      outlined
                      rounded
                      full-width
                      append-icon="icon-arrow-down"
                      :menu-props="{
                        contentClass: 'sort-type-select__menu'
                      }"
                      class="sort-type-select"
                      @change="fetchReviews"
                    >
                    </v-select>
                  </div>

                  <ui-button
                    v-if="canReplyToReviewsManually"
                    color="primary"
                    class="ml-6"
                    icon="icon-bulk-answer"
                    @click="openDialog('bulkReply')"
                    :disabled="selectedReviewsIds.length === 0"
                  >
                    {{ $t("message.reviews.bulkReply.btnLabel") }}
                  </ui-button>
                  <ui-button
                    color="primary"
                    class="ml-6"
                    @click="openDialog('assignCategories')"
                    :disabled="selectedReviewsIds.length === 0"
                  >
                    {{ $t("message.reviews.assignCategories") }}
                  </ui-button>
                </Pagination>
              </v-col>
            </v-row>
            <div class="select-all-container">
              <div class="d-flex justify-space-between items-center">
                <v-checkbox
                  v-if="!idsLoading"
                  :inputValue="isSelectedAll"
                  :label="$t('message.common.selectAll')"
                  hide-details
                  @change="toggleAll"
                />
                <div class="d-flex align-center" v-else>
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                  />
                  <span class="theme--light v-label ml-3">{{
                    $t("message.common.selectAll")
                  }}</span>
                </div>

                <ItemsCounter :count="selectedReviewsIds.length" />
              </div>
            </div>
            <v-row>
              <v-col cols="12" class="pt-0">
                <div style="min-height:300px">
                  <template v-if="showReviewsList">
                    <ReviewItem
                      v-for="review in reviewsList"
                      :key="review.id"
                      :item="review"
                      :is-review-selected="
                        selectedReviewsIds.includes(review.id)
                      "
                      :checkbox-disabled="idsLoading"
                      :ai-limits="aiLimits"
                      @toggleReviewSelect="handleToggleReviewSelect"
                      @updated="fetchIdsToReply"
                      @ai-reply="getAiLimit"
                      @reply-sent="handleReplySent"
                      @reply-deleted="handleReplyDeleted"
                    >
                      <template #tags>
                        <ReviewTagsPreview
                          v-if="canTagReviews"
                          :is-loading="isLoading"
                          :available-tags="tags"
                          :review="review"
                          @open-dialog="$emit('open-dialog', 'tagsManager')"
                          @tags-preview-close="closeAssignCategories"
                        />
                      </template>
                    </ReviewItem>
                  </template>
                  <h3 v-else class="no-data">
                    {{ $t("message.reviews.noData") }}
                  </h3>
                  <SectionLoader :status="loading" />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <Pagination
                  class="flex-grow-1"
                  :page="page"
                  :per-page="perPage"
                  :options="perPageOptions"
                  :total-count="totalCount"
                  :show-options="false"
                  @change="handlePageChange"
                ></Pagination>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <v-card style="min-height: 650px;" class="d-flex px-12">
              <div class="mx-auto d-flex align-center">
                <div class="no-data d-flex flex-column align-center">
                  <h2 class="subtitle mb-6">
                    {{ $t("message.reviews.noFacilities.title") }}
                  </h2>
                  <p class="no-data__description text-center">
                    {{ $t("message.reviews.noFacilities.desc") }}
                  </p>
                  <v-btn color="secondary" @click="goToGMBAccounts">
                    {{ $t("message.reviews.noFacilities.button") }}
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <SectionLoader :status="!compLoaded" />
    </v-row>

    <FilterDialog
      v-if="dialog.filter"
      :gmb-addresses-ids="gmbAddressesIds"
      :cities="citiesChecked"
      :facilities-groups="facilitiesGroupsIds"
      :rating="ratingValues"
      :chosen-reviews-tags="reviewsTagsIds"
      :chosen-only-comments="onlyComments"
      :chosen-only-accurate="onlyAccurate"
      :chosen-only-notes="onlyNotes"
      :chosen-search-type="searchType"
      :chosen-reviews-tags-search-type="reviewsTagsSearchType"
      :chosen-only-updated="onlyUpdated"
      :chosen-only-created="onlyCreated"
      :show-comments-options="true"
      :show-ratings="true"
      :show-reviews-tags="true"
      :tags="tags"
      :select-deleted-reviews="showDeleted"
      show-has-note
      show-deleted-option
      show-date-options
      @setGmbAddressesIds="gmbAddressesIds = $event"
      @setCities="citiesChecked = $event"
      @setFacilitiesGroupsIds="facilitiesGroupsIds = $event"
      @setRatingValues="ratingValues = $event"
      @setReviewsTagsIds="reviewsTagsIds = $event"
      @setOnlyComments="onlyComments = $event"
      @setOnlyAccurate="onlyAccurate = $event"
      @setReviewsTagsSearchType="reviewsTagsSearchType = $event"
      @setSearchType="searchType = $event"
      @setOnlyNotes="onlyNotes = $event"
      @setShowDeleted="showDeleted = $event"
      @setOnlyUpdated="onlyUpdated = $event"
      @setOnlyCreated="onlyCreated = $event"
      @change="applyFilters"
      :handleClose="closeFilterDialog"
    />

    <AssignCategoriesDialog
      v-if="dialog.assignCategories"
      :is-open="dialog.assignCategories"
      :selected-reviews-ids="selectedReviewsIds"
      :is-loading="isLoading"
      @assign-categories="closeAssignCategories"
      @close="close"
      @list-update="handleListUpdate"
    />

    <BulkReplyDialog
      :is-open="dialog.bulkReply"
      @close="dialog.bulkReply = false"
      @reply="handleBulkReply($event, gtmActions.REPLY_MANUALLY)"
      @reply-templates="handleBulkReply($event, gtmActions.REPLY_TEMPLATE)"
    />
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import cuid from "cuid";

import ReviewItem from "@/views/reviews/ReviewItem";
import FilterDialog from "@/components/Filter/dialog";
import SectionLoader from "@/components/Loader/SectionLoader";
import Pagination from "@/components/List/Pagination";
import ItemsCounter from "@/components/List/ItemsCounter";
import ReviewTagsPreview from "@/components/Reviews/ReviewTagsPreview.vue";

import serviceHandler from "@/helpers/handlers/service";
import { gtmEvent__utmEmailNotification } from "@/helpers/gtm-events";
import { isUtmTaggedUrl } from "@/helpers/utm-tags";
import tagsService from "@/services/tags";
import { REVIEWS_TAGS_SEARCH_TYPE } from "@/enums/reviews.enum";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { setCompanyIdFromQuery as setCompanyIdFromQueryMixin } from "@/helpers/mixins/set-company-id-from-query";
import reviewsService from "@/services/reviews";
import BulkReplyDialog from "@/components/Reviews/BulkReply/Dialog";

const reviewQuery = ["", "true", "false"];
const gtmActions = {
  REPLY_MANUALLY: "reply_manually",
  REPLY_TEMPLATE: "reply_template"
};
import { FACILITIES_FILTER_SEARCH_TYPES } from "@/enums/facilty-details-status.enum";
import { PLAN_RESTRICTIONS } from "@/enums/plan-restrictions";
import { planRestrictionsCheck } from "@/helpers/plan-restrictions-check";
import { Permission } from "@/enums/permission";
import { permissionCheck } from "@/helpers/permission-check";
export default {
  name: "BaseReviewsList",
  components: {
    ReviewItem,
    FilterDialog,
    SectionLoader,
    Pagination,
    ItemsCounter,
    ReviewTagsPreview,
    BulkReplyDialog,
    AssignCategoriesDialog: () =>
      import("@/components/Reviews/ReplyTemplates/AssignCategoriesDialog.vue")
  },
  mixins: [permissionsInfo, gtmEngagementMixin, setCompanyIdFromQueryMixin],
  provide: {
    showReviewAddedWithRb: isFeatureFlagEnabled(
      FEATURE_FLAG.SHOW_REVIEW_ADDED_WITH_RB
    ),
    showReplyTemplates: isFeatureFlagEnabled(
      FEATURE_FLAG.REVIEWS_SHOW_REPLY_TEMPLATES
    )
  },
  props: {
    initialDate: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      FEATURE_FLAG,
      menu: false,
      searchValue: "",
      searchMode: false,
      dialog: {
        filter: false,
        phraseManage: false,
        bulkReply: false,
        assignCategories: false,
        tagsPreview: false
      },
      active: 0,
      date: this.initialDate,
      gmbAddressesIds: [],
      citiesChecked: [],
      facilitiesGroupsIds: [],
      ratingValues: [],
      onlyComments: null,
      onlyAccurate: false,
      onlyNotes: false,
      showDeleted: false,
      onlyUpdated: false,
      onlyCreated: false,
      searchType: null,
      reviewsTagsSearchType: REVIEWS_TAGS_SEARCH_TYPE.INCLUDE_ALL_TAGS,
      reviewsTagsIds: [],
      compLoaded: false,
      isLoading: false,
      updatedReviewsIds: [],
      tabs: [
        {
          id: cuid(),
          name: "message.reviews.all"
        },
        {
          id: cuid(),
          name: "message.reviews.answered"
        },
        {
          id: cuid(),
          name: "message.reviews.unanswered"
        }
      ],
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      facilitiesCount: 0,
      selectedReview: null,
      tags: [],
      selectedReviewsIds: [],
      idsToReply: [],
      idsLoading: false,
      gtmActions,
      sortType: null,
      sortTypes: null,
      searchTypes: FACILITIES_FILTER_SEARCH_TYPES,
      loadingExport: false,
      isCounterLoading: true,
      aiLimits: { remaining_limit: 0, monthly_limit: 0 }
    };
  },

  async created() {
    this.setDefaultSearchType();
    this.setSortTypes();
    this.ratingValues = this.getRatingValues();
    this.gmbAddressesIds = this.getGmbAddressIds();
    await this.fetchReviews();
    this.facilitiesCount = this.$store.state.company.company.facilities_count;
    if (this.canReplyReviews) {
      await this.getAiLimit();
    }

    this.compLoaded = true;
  },

  mounted() {
    this.fetchTags();

    if (isUtmTaggedUrl(this.$route.query)) {
      gtmEvent__utmEmailNotification(this.$route.query);
    }
    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  computed: {
    totalCount() {
      const count = this.reviews.count - this.updatedReviewsIds.length;
      if (count < 0) return 0;
      return count;
    },
    reviewsList() {
      return this.reviews.results.filter(review => {
        if (this.filterOutAnswered) {
          return !this.updatedReviewsIds.includes(review.id);
        }
        return true;
      });
    },
    canReplyToReviewsManually() {
      return (
        planRestrictionsCheck(
          PLAN_RESTRICTIONS.CAN_REPLY_TO_REVIEWS_MANUALLY
        ) && permissionCheck(Permission.REPLY_REVIEWS)
      );
    },
    canUseAiReviewReply() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_AI_REVIEW_REPLY);
    },
    canUseReviewTemplates() {
      return planRestrictionsCheck(PLAN_RESTRICTIONS.CAN_USE_REVIEW_TEMPLATES);
    },
    showReviewsList() {
      const resultsLength =
        this.reviews && this.reviews.results && this.reviews.results.length;
      return (
        (!this.filterOutAnswered && resultsLength) ||
        (this.filterOutAnswered &&
          this.updatedReviewsIds.length < resultsLength &&
          (this.updatedReviewsIds.length > 0 || resultsLength > 0))
      );
    },
    ...mapState({
      reviews: state => state.reviews.data,
      allReviewsTotalCount: state => state.reviews.allReviewsTotalCount,
      loading: state => state.reviews.loading,
      selectedLocale: state => state.settings.selectedLocale
    }),
    isAiConfigurationDialogAvailable() {
      return this.canConfigureAiReviewReply;
    },
    percentageValue() {
      return this.isCounterLoading
        ? 0
        : Math.round((this.totalCount / this.allReviewsTotalCount) * 100);
    },
    showPercentage() {
      return this.active > 0;
    },
    basicCounterLabel() {
      if (this.active == 1) {
        return "message.reviews.withReplyReviewsCount";
      } else if (this.active == 2) {
        return "message.reviews.withoutReplyReviewsCount";
      }
      return "message.reviews.totalReviewsCount";
    },
    filterOutAnswered() {
      return this.active === 2;
    },
    requestParams() {
      const isNoTags =
        this.reviewsTagsSearchType === REVIEWS_TAGS_SEARCH_TYPE.NO_TAGS;
      return {
        query: this.searchValue,
        page: this.page,
        perPage: this.perPage,
        date: this.date,
        reply: reviewQuery[this.active],
        cities: this.citiesChecked.join(",") || this.$route.query.city,
        gmb_address_ids:
          this.gmbAddressesIds.join(",") || this.$route.query.facility,
        groups:
          this.facilitiesGroupsIds.join(",") ||
          this.$route.query.facility_group,
        rating: this.ratingValues.join(","),
        comment: this.onlyComments,
        is_accurate: this.onlyAccurate || null,
        has_note: this.onlyNotes || null,
        tags_search_type: isNoTags
          ? null
          : this.reviewsTagsIds && this.reviewsTagsIds.length
          ? this.reviewsTagsSearchType
          : null,
        ...(isNoTags && {
          has_tags: isNoTags ? false : null
        }),
        tags_ids: this.reviewsTagsIds.join(",") || this.$route.query.tag_id,
        order_by: this.sortType,
        deleted: this.showDeleted,
        only_created: this.onlyCreated,
        only_updated: this.onlyUpdated
      };
    },
    isSelectedAll() {
      return (
        !!this.idsToReply.length &&
        this.idsToReply.length === this.selectedReviewsIds.length
      );
    }
  },

  methods: {
    showTagsPreviewDialog() {
      this.dialog.tagsPreview = true;
    },
    handleReplyDeleted(reviewId) {
      this.updatedReviewsIds = this.updatedReviewsIds.filter(
        id => id !== reviewId
      );
    },
    async handleReplySent(reviewId) {
      if (
        !this.updatedReviewsIds.includes(reviewId) &&
        this.filterOutAnswered
      ) {
        this.updatedReviewsIds.push(reviewId);
        await this.fetchReviews(false, false);
      }
    },
    async onSentimentChange(sentiment) {
      await reviewsService.patchReviewCategory(sentiment);
    },
    async getAiLimit() {
      if (!this.canUseAiReviewReply) {
        return;
      }
      this.aiLimits = await serviceHandler(reviewsService.getAiLimit());
    },
    async exportToCsv() {
      try {
        const { export_id } = await serviceHandler(
          reviewsService.exportReviews(this.requestParams)
        );
        this.$emit("export", export_id);
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.REVIEW_LIST_EXPORT,
          format: "xlsx"
        });
      } catch (err) {
        return;
      }
    },
    setDefaultSearchType() {
      if (this.$route.query.city) {
        this.searchType = this.searchTypes.CITY;
      } else if (this.$route.query.facility) {
        this.searchType = this.searchTypes.FACILITIES;
      } else if (this.$route.query.facility_group) {
        this.searchType = this.searchTypes.FACILITIES_GROUPS;
      }
    },
    async applyFilters() {
      this.page = 1;
      if (this.isSelectedAll && this.idsToReply.length) {
        await this.fetchIdsToReply();
        this.selectedReviewsIds = this.idsToReply;
      }
      await this.fetchReviews();
    },
    async onReviewSearch() {
      this.page = 1;
      this.searchMode = true;
      await this.setReviews({
        payload: this.requestParams,
        searchMode: this.searchMode
      });
      if (this.requestParams.reply) {
        this.isCounterLoading = true;
        await this.setReviews({
          payload: {
            ...this.requestParams,
            only_count: true,
            reply: undefined
          },
          searchMode: this.searchMode
        });
        this.isCounterLoading = false;
      }
      await this.fetchTags();
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEWS_SEARCH,
        query: this.searchValue,
        num_results: this.totalCount
      });
    },
    async clearSearch() {
      this.searchMode = false;
      this.searchValue = "";
      await this.fetchReviews();
      await this.fetchTags();
    },
    async handleListUpdate(event) {
      this.isLoading = true;
      this.tags = event;
      await this.fetchReviews();
      await this.fetchTags();
      this.isLoading = false;
    },
    async closeAssignCategories() {
      this.isLoading = true;
      await this.fetchReviews();
      await this.fetchTags();
      this.dialog.assignCategories = false;
      this.selectedReviewsIds = [];
      this.isLoading = false;
    },
    close() {
      this.dialog.assignCategories = false;
    },
    isFeatureFlagEnabled,
    ...mapActions({
      setReviews: "reviews/fetchReviews",
      postBulkReply: "reviews/postBulkReply",
      getBusinessAddresses: "businessAddresses/getBusinessAddresses",
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore"
    }),
    getGmbAddressIds() {
      return this.$route.query.ids
        ? this.$route.query.ids.split(",").map(id => +id)
        : [];
    },
    getRatingValues() {
      return this.$route.query.ratings
        ? this.$route.query.ratings.split(",").map(rating => +rating)
        : [];
    },
    goToGMBAccounts() {
      this.$router.push({ name: "profile-gmb-accounts" });
    },
    async fetchReviews(withIds = false, showLoading = true) {
      if (withIds) {
        this.fetchIdsToReply();
      }
      await this.setReviews({
        payload: this.requestParams,
        searchMode: this.searchMode,
        showLoading
      });
      if (this.requestParams.reply) {
        this.isCounterLoading = true;

        await this.setReviews({
          payload: {
            ...this.requestParams,
            only_count: true,
            reply: undefined
          },
          searchMode: this.searchMode
        });
        this.isCounterLoading = false;
      }
      this.updatedReviewsIds = [];
    },
    async fetchIdsToReply() {
      this.idsLoading = true;
      const wasSelectedAll = this.isSelectedAll;
      if (this.requestParams.reply === "true") {
        this.idsToReply = [];
      } else {
        if (!this.searchMode) {
          this.idsToReply = await serviceHandler(
            reviewsService.getReviews({
              ...this.requestParams,
              only_ids: true
            })
          );
        } else {
          const { ids } = await serviceHandler(
            reviewsService.getReviews(
              {
                ...this.requestParams,
                only_ids: true
              },
              this.searchMode
            )
          );
          this.idsToReply = ids;
        }
      }
      if (wasSelectedAll) {
        this.selectedReviewsIds = [...this.idsToReply];
      } else {
        this.selectedReviewsIds = [];
      }
      this.idsLoading = false;
    },
    openDialog(type) {
      this.dialog[type] = true;
    },
    closeFilterDialog() {
      this.dialog.filter = false;
    },
    async handlePageChange(
      { page, perPage } = { page: this.page, perPage: this.perPage }
    ) {
      this.page = page;
      this.perPage = perPage;
      await this.fetchReviews();
    },
    handleDate(date) {
      this.date = date;
      this.page = 1;
      this.fetchReviews(true);
    },

    closeMenu() {
      this.menu = false;
    },

    async fetchTags() {
      if (!this.canTagReviews) {
        return;
      }
      this.isLoading = true;
      this.tags = await serviceHandler(tagsService.getTags());
      this.isLoading = false;
    },

    handleToggleReviewSelect(reviewId) {
      if (
        Array.isArray(this.selectedReviewsIds) &&
        this.selectedReviewsIds.includes(reviewId)
      ) {
        this.selectedReviewsIds = this.selectedReviewsIds.filter(
          id => id !== reviewId
        );
      } else {
        this.selectedReviewsIds.push(reviewId);
      }
    },

    async toggleAll() {
      if (!this.isSelectedAll && !this.idsToReply.length) {
        await this.fetchIdsToReply();
      } else {
        this.idsToReply = [];
      }
      this.selectedReviewsIds = !this.isSelectedAll ? [...this.idsToReply] : [];
    },

    async handleBulkReply(data, actionType) {
      if (actionType === gtmActions.REPLY_MANUALLY) {
        await this.postBulkReply({
          ids: [...this.selectedReviewsIds],
          reply: data
        });
      } else if (actionType === gtmActions.REPLY_TEMPLATE) {
        await serviceHandler(
          reviewsService.postTemplateBulkReply({
            ids: [...this.selectedReviewsIds],
            templateIds: [...data]
          })
        );
        this.fetchReviews(true);
      } else {
        return;
      }

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_REPLY_BULK,
        num_selected: this.selectedReviewsIds.length,
        action: actionType
      });
      this.updatedReviewsIds = [
        ...this.updatedReviewsIds,
        ...this.selectedReviewsIds
      ];
      this.selectedReviewsIds = [];
      this.fetchIdsToReply();
    },
    setSortTypes() {
      this.sortTypes = [
        { label: this.$t("message.reviews.ratingDesc"), value: "-rating" },
        { label: this.$t("message.reviews.ratingAsc"), value: "rating" },
        {
          label: this.$t("message.reviews.createDateDesc"),
          value: "-gmb_update_time"
        },
        {
          label: this.$t("message.reviews.createDateAsc"),
          value: "gmb_update_time"
        }
      ];
    }
  },

  watch: {
    active() {
      this.page = 1;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_LIST_FILTER_BY_REPLY,
        filter: this.tabs[this.active].name.split(".").slice(-1)[0]
      });

      this.fetchReviews(true);
    },
    gmbAddressesIds() {
      this.page = 1;
    },
    ratingValues() {
      this.page = 1;
    }
  }
};
</script>

<style lang="scss" scoped>
.base-reviews-list {
  min-height: 600px;
}

.actions-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
}
.no-data {
  text-align: center;
  padding-top: 40px;
  max-width: 500px;
  margin: auto;
}

.min-w-230 {
  min-width: 230px;
}

.select-all-container {
  padding-bottom: $spacing * 2.25;
  margin-top: $spacing;
  border-bottom: 1px solid $base-light-2;

  ::v-deep {
    .v-input--selection-controls {
      padding-top: 0;
      margin-top: 0;

      &__input {
        margin-right: $spacing * 1.5;
      }
    }

    .v-label {
      @include font-size(14px);
      white-space: nowrap;
    }
  }
}

.sort-type {
  width: 260px;
  display: flex;
  flex-direction: row;
  align-items: center;
}

::v-deep .sort-type-select {
  width: 100%;
  height: 100%;
  flex-shrink: 0;

  &.v-text-field--outlined.v-input--dense.v-text-field--outlined {
    > .v-input__control {
      > .v-input__slot {
        padding-left: $spacing;
        padding-right: 0;
        min-height: 46px;
        height: 46px;
        @include font-size(14px);
        line-height: 17px;
        font-weight: normal;
      }
    }

    fieldset {
      border-color: rgba($text, 0.08);
      border-width: 1px;
      border-radius: $container-border-radius;
    }
  }

  &.v-text-field--outlined.v-input--dense.v-input--is-focused fieldset {
    border-color: $primary;
  }

  &.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined
    .v-input__append-inner {
    width: 30px;
    margin-top: auto;
    margin-bottom: auto;
    margin-right: $spacing * 1.5;
    padding-left: $spacing;
  }

  .v-select__selection--comma {
    margin-left: $spacing * 1.5 !important;
  }

  .v-label {
    @include font-size(14px);
    line-height: 17px;
    font-weight: normal;
    color: $dark-blue-3;
    padding-left: $spacing * 1.5;
    margin: $spacing / 8 0;
  }
}
.counter {
  flex-shrink: 0;
}
.searchbox {
  display: flex;
  width: 100%;
  position: relative;
  margin-right: $spacing * 2;

  ::v-deep input {
    padding-right: $spacing * 12;
  }

  &__btn {
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
    right: $spacing * 0.5;
    height: 40px !important;
  }
}
.counters {
  display: flex;
  flex-direction: column;

  & > * {
    flex-grow: 1;
    flex-shrink: 0;
    white-space: nowrap;
  }
}
</style>
